import React, { useMemo, useRef } from 'react';
import SearchSuggestField from './SearchSuggestField';

// ─────────────────────────────────────────────────────────────────────────────
// Combo con búsqueda, controlado por id.
//
// Es un envoltorio delgado sobre SearchSuggestField, que es el ÚNICO motor de
// desplegable del sistema. Acá sólo vive la traducción entre dos contratos:
//
//   SearchableCombo      trabaja por id      value={'12'}  onChange('12')
//   SearchSuggestField   trabaja por texto   value={'Filtro de aceite'}
//                                            onSelectItem({id: 12, ...})
//
// Los dos contratos se mantienen porque son genuinamente distintos y cada uno
// le queda cómodo a su lado: un formulario que guarda una FK piensa en ids, y
// un buscador libre piensa en texto. Lo que NO se mantiene es tener dos motores:
// antes este archivo tenía su propio estado, su propio filtrado, su propio
// dropdown y su propio manejo de teclado, y cada arreglo había que hacerlo dos
// veces (o, peor, se hacía en uno solo y las pantallas quedaban comportándose
// distinto según qué combo les hubiera tocado).
//
// Efecto secundario de la unificación: este combo pasa a dibujar su lista en un
// portal, así que ya no la recorta un contenedor con overflow —el motivo por el
// que existían las dos variantes de posicionamiento.
// ─────────────────────────────────────────────────────────────────────────────

export default function SearchableCombo({
  items,
  value,
  onChange,
  disabled,
  placeholder,
  getLabel,
  getFilterText,
  renderItem,
  autoWidth = false,
  onSearch,
  onInputDoubleClick,
  onItemDoubleClick,
  keepOpenOnSelect = false,
  hasMore = false,
  searchError = false,
  onLoadMore,
  loadingMore = false,
  error = false,
  size = 'sm', // 'sm' (compacto, uso en escritorio) | 'lg' (táctil, formularios mobile-first)
  saltarAlSiguiente = true,
}) {
  const lista = useMemo(() => items || [], [items]);

  // Texto visible del valor elegido. Si el id seleccionado no está en `items`
  // —pasa con búsqueda server-side, cuando la tanda actual no lo incluye— se
  // conserva el último label conocido en vez de dejar el campo en blanco: un
  // campo que se ve vacío se vuelve a cargar, y termina en registros duplicados.
  const ultimoLabelRef = useRef('');
  const etiquetaSeleccionada = useMemo(() => {
    if (!value) {
      ultimoLabelRef.current = '';
      return '';
    }
    const encontrado = lista.find((i) => String(i.id) === String(value));
    if (encontrado) {
      ultimoLabelRef.current = getLabel(encontrado);
    }
    return ultimoLabelRef.current;
  }, [value, lista, getLabel]);

  return (
    <SearchSuggestField
      value={etiquetaSeleccionada}
      onValueChange={(texto) => {
        // Sólo interesa el borrado manual: limpiar el campo limpia la selección.
        if (!String(texto || '').trim()) onChange('');
      }}
      items={lista}
      selectedKey={value}
      onSelectItem={(item) => onChange(String(item.id))}
      onDoubleClickItem={onItemDoubleClick ? (item) => onItemDoubleClick(String(item.id)) : undefined}
      getItemKey={(item) => String(item.id)}
      getSearchText={(item) => (getFilterText ? getFilterText(item) : getLabel(item))}
      renderItem={renderItem || ((item) => <span>{getLabel(item)}</span>)}
      autoWidth={autoWidth}
      placeholder={placeholder || 'Buscar...'}
      disabled={disabled}
      size={size}
      // Al enfocar despliega la lista completa: este combo se usa para elegir de
      // un catálogo, no para adivinar el término exacto.
      showAllOnFocus
      closeOnSelect={!keepOpenOnSelect}
      invalid={error}
      onSearch={onSearch}
      hasMore={hasMore}
      loadingMore={loadingMore}
      searchError={searchError}
      onLoadMore={onLoadMore}
      saltarAlSiguiente={saltarAlSiguiente}
      onDoubleClickInput={onInputDoubleClick}
    />
  );
}
